import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { fetchLandingAndStylesRedirectsIfNeeded, isRedirected } from '../../redux/redirect/actions';
import useFetchingAction from '../../hooks/useFetchingAction/useFetchingAction';
import useListingPageUrlState from '../../hooks/ListingPage/useListingPageUrlState';
import StateInterface from '../../redux/types';
import { LandingRedirectsData } from '../../redux/redirect/types';
import useRouter from '../../hooks/useRouter';
import findLandingPageRedirect from '../../../common/LandingPageRedirectService';
import { fetchSearchRedirectsIfNeeded } from '../../redux/search/actions';
import { MINIMUM_SEARCH_QUERY_LENGTH, Suggestions } from '../../redux/search/types';
import { getSuggestionRedirect } from '../Search/searchService';
import { parseRedirect } from '../../../common/apiAdapters/shopApiAdapter';

// eslint-disable-next-line complexity
const useLandingPageRedirects = () => {
    const dispatch = useDispatch();
    const {
        location: { pathname, search: queryString },
    } = useRouter();
    const [urlState] = useListingPageUrlState();
    const landingRedirects = useSelector<StateInterface, LandingRedirectsData | undefined>(
        state => state.redirect.landingRedirects
    );

    const searchSuggestions = useSelector<StateInterface, Suggestions | undefined>(
        state => state.searchSuggestion.searchRedirect
    );

    const search = urlState.search || '';

    useEffect(() => {
        if (search.length > MINIMUM_SEARCH_QUERY_LENGTH) {
            dispatch(fetchSearchRedirectsIfNeeded(search));
        }
    }, [dispatch, search]);

    useFetchingAction(fetchLandingAndStylesRedirectsIfNeeded(Boolean(urlState.search)));

    if (!search || !landingRedirects || !pathname.includes('all-products')) {
        return false;
    }

    /**
     * as mentioned in docs/redirects.md this is where redirect for style / landing
     * pages are handled
     */
    let landingRedirectPath = findLandingPageRedirect(landingRedirects, search);
    if (landingRedirectPath) {
        landingRedirectPath = parseRedirect({
            currentPathname: pathname,
            currentSearch: queryString,
            targetPath: landingRedirectPath,
        });
        dispatch(
            isRedirected({
                targetPath: landingRedirectPath,
                requestPath: pathname,
                httpCode: 302,
                source: 'landing page redirect from ex bob landing page redirects config',
            })
        );
        return true;
    }

    if (!searchSuggestions) {
        return false;
    }

    /**
     * as mentioned in docs/redirects.md this is where redirect for search queries
     * are handled
     */
    const searchRedirect = getSuggestionRedirect(searchSuggestions, search, queryString);
    if (searchRedirect && searchRedirect.url && searchRedirect.url !== '') {
        dispatch(
            isRedirected({
                targetPath: searchRedirect.url,
                requestPath: pathname,
                httpCode: 302,
                source: 'search redirect configured inside search service',
            })
        );
        return true;
    }

    return false;
};

export default useLandingPageRedirects;
